import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Hotel } from '../types/chat';
import { useTranslation } from 'react-i18next';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`hotel-settings-tabpanel-${index}`}
      aria-labelledby={`hotel-settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const HotelSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Common TextField styling
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
      '&:hover': {
        backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
        '& fieldset': {
          borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : '#cbd5e1',
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
        '& fieldset': {
          borderColor: isDark ? '#3b82f6' : '#667eea',
          borderWidth: 2,
        },
      },
      '& fieldset': {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
        borderWidth: 1,
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      color: isDark ? theme.palette.text.secondary : '#64748b',
      '&.Mui-focused': {
        color: isDark ? '#3b82f6' : '#667eea',
        fontWeight: 500,
      },
    },
    '& .MuiInputBase-input': {
      fontWeight: 400,
      color: theme.palette.text.primary,
    },
  };

  const [hotelData, setHotelData] = useState<Partial<Hotel>>({
    name: '',
    code: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    activities: [],
    rules: [],
    amenities: [],
    policies: {
      cancellation: '',
      checkIn: '',
      checkOut: '',
      payment: '',
      children: '',
      pets: '',
      smoking: '',
      other: [],
    },
    settings: {
      timezone: '',
      currency: '',
      language: '',
      checkInTime: '',
      checkOutTime: '',
      aiEnabled: true,
      maxGuests: 0,
      starRating: 0,
    },
  });

  // Input states for adding items to arrays
  const [newActivity, setNewActivity] = useState('');
  const [newActivityDetails, setNewActivityDetails] = useState('');
  const [newRule, setNewRule] = useState('');
  const [newAmenity, setNewAmenity] = useState('');
  const [newPolicyOther, setNewPolicyOther] = useState('');

  const fetchHotelData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!user?.hotelId) {
        setError(t('hotelSettings.noHotelAssociated'));
        return;
      }
      const response = await api.get(`/hotels/${user.hotelId}`);
      setHotelData({
        ...response.data,
        activities: response.data.activities || [],
        rules: response.data.rules || [],
        amenities: response.data.amenities || [],
        policies: response.data.policies || {
          cancellation: '',
          checkIn: '',
          checkOut: '',
          payment: '',
          children: '',
          pets: '',
          smoking: '',
          other: [],
        },
        settings: response.data.settings || {
          timezone: '',
          currency: '',
          language: '',
          checkInTime: '',
          checkOutTime: '',
          aiEnabled: true,
          maxGuests: 0,
          starRating: 0,
        },
      });
    } catch (err: any) {
      setError(err.response?.data?.message || t('hotelSettings.failedToFetch'));
      console.error('Error fetching hotel:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.hotelId, t]);

  useEffect(() => {
    fetchHotelData();
  }, [fetchHotelData]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      if (!user?.hotelId) {
        setError(t('hotelSettings.noHotelAssociated'));
        return;
      }

      await api.patch(`/hotels/${user.hotelId}`, hotelData);
      setSuccess(t('hotelSettings.savedSuccessfully'));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || t('hotelSettings.failedToSave'));
      console.error('Error saving hotel:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddActivity = () => {
    if (newActivity.trim()) {
      const activityObj = {
        name: newActivity.trim(),
        details: newActivityDetails.trim() || '',
      };
      setHotelData({
        ...hotelData,
        activities: [...(hotelData.activities || []), activityObj],
      });
      setNewActivity('');
      setNewActivityDetails('');
    }
  };

  const handleRemoveActivity = (index: number) => {
    const updated = [...(hotelData.activities || [])];
    updated.splice(index, 1);
    setHotelData({ ...hotelData, activities: updated });
  };

  const handleAddRule = () => {
    if (newRule.trim()) {
      setHotelData({
        ...hotelData,
        rules: [...(hotelData.rules || []), newRule.trim()],
      });
      setNewRule('');
    }
  };

  const handleRemoveRule = (index: number) => {
    const updated = [...(hotelData.rules || [])];
    updated.splice(index, 1);
    setHotelData({ ...hotelData, rules: updated });
  };

  const handleAddAmenity = () => {
    if (newAmenity.trim()) {
      setHotelData({
        ...hotelData,
        amenities: [...(hotelData.amenities || []), newAmenity.trim()],
      });
      setNewAmenity('');
    }
  };

  const handleRemoveAmenity = (index: number) => {
    const updated = [...(hotelData.amenities || [])];
    updated.splice(index, 1);
    setHotelData({ ...hotelData, amenities: updated });
  };

  const handleAddPolicyOther = () => {
    if (newPolicyOther.trim()) {
      setHotelData({
        ...hotelData,
        policies: {
          ...hotelData.policies,
          other: [...(hotelData.policies?.other || []), newPolicyOther.trim()],
        },
      });
      setNewPolicyOther('');
    }
  };

  const handleRemovePolicyOther = (index: number) => {
    const updated = [...(hotelData.policies?.other || [])];
    updated.splice(index, 1);
    setHotelData({
      ...hotelData,
      policies: { ...hotelData.policies, other: updated },
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100vh', 
      overflow: 'auto',
      bgcolor: theme.palette.background.default,
    }}>
      <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {t('hotelSettings.title')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 2,
              bgcolor: isDark ? '#1e3b8a' : '#3b82f6',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: isDark ? '#1e40af' : '#2563eb',
                boxShadow: 'none',
              },
              '&:disabled': {
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#cbd5e1',
                color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#94a3b8',
              },
            }}
          >
            {saving ? t('hotelSettings.saving') : t('hotelSettings.saveChanges')}
          </Button>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              borderRadius: 2,
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 2,
              borderRadius: 2,
            }} 
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        <Paper sx={{ 
          p: 3,
          borderRadius: 2,
          bgcolor: theme.palette.background.paper,
          boxShadow: isDark ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
        }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => setTabValue(newValue)} 
            sx={{ 
              mb: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: isDark ? '#3b82f6' : '#667eea',
                  fontWeight: 500,
                },
              },
              '& .MuiTabs-indicator': {
                bgcolor: isDark ? '#3b82f6' : '#667eea',
                height: 2,
              },
            }}
          >
            <Tab label={t('hotelSettings.basicInformation')} />
            <Tab label={t('hotelSettings.activitiesAmenities')} />
            <Tab label={t('hotelSettings.rulesPolicies')} />
            <Tab label={t('hotelSettings.settings')} />
          </Tabs>

          {/* Tab 0: Basic Information */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.hotelName')}
                  value={hotelData.name || ''}
                  onChange={(e) => setHotelData({ ...hotelData, name: e.target.value })}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.hotelCode')}
                  value={hotelData.code || ''}
                  disabled
                  helperText={t('hotelSettings.hotelCodeCannotChange')}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.description')}
                  multiline
                  rows={4}
                  value={hotelData.description || ''}
                  onChange={(e) => setHotelData({ ...hotelData, description: e.target.value })}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.address')}
                  value={hotelData.address || ''}
                  onChange={(e) => setHotelData({ ...hotelData, address: e.target.value })}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.city')}
                  value={hotelData.city || ''}
                  onChange={(e) => setHotelData({ ...hotelData, city: e.target.value })}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.state')}
                  value={hotelData.state || ''}
                  onChange={(e) => setHotelData({ ...hotelData, state: e.target.value })}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.zipCode')}
                  value={hotelData.zipCode || ''}
                  onChange={(e) => setHotelData({ ...hotelData, zipCode: e.target.value })}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.country')}
                  value={hotelData.country || ''}
                  onChange={(e) => setHotelData({ ...hotelData, country: e.target.value })}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.phone')}
                  value={hotelData.phone || ''}
                  onChange={(e) => setHotelData({ ...hotelData, phone: e.target.value })}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.email')}
                  type="email"
                  value={hotelData.email || ''}
                  onChange={(e) => setHotelData({ ...hotelData, email: e.target.value })}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.website')}
                  value={hotelData.website || ''}
                  onChange={(e) => setHotelData({ ...hotelData, website: e.target.value })}
                  sx={textFieldSx}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 1: Activities & Amenities */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>{t('hotelSettings.activities')}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder={t('hotelSettings.activityNamePlaceholder')}
                    value={newActivity}
                    onChange={(e) => setNewActivity(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAddActivity()}
                    sx={textFieldSx}
                  />
                  <TextField
                    fullWidth
                    placeholder={t('hotelSettings.activityDetailsPlaceholder')}
                    multiline
                    rows={3}
                    value={newActivityDetails}
                    onChange={(e) => setNewActivityDetails(e.target.value)}
                    sx={textFieldSx}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddActivity}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 3,
                        bgcolor: isDark ? '#1e3b8a' : '#3b82f6',
                        boxShadow: isDark ? '0 4px 12px rgba(30, 59, 138, 0.4)' : '0 4px 12px rgba(59, 130, 246, 0.3)',
                        '&:hover': {
                          bgcolor: isDark ? '#1e40af' : '#2563eb',
                          boxShadow: isDark ? '0 6px 16px rgba(30, 59, 138, 0.5)' : '0 6px 16px rgba(59, 130, 246, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {t('hotelSettings.add')}
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {hotelData.activities?.map((activity, index) => {
                    const activityName = typeof activity === 'string' ? activity : activity.name;
                    const activityDetails = typeof activity === 'string' ? '' : (activity.details || '');
                    return (
                      <Paper
                        key={index}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                          bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#ffffff',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                            {activityName}
                          </Typography>
                          {activityDetails && (
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                              {activityDetails}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          onClick={() => handleRemoveActivity(index)}
                          sx={{
                            color: isDark ? '#f87171' : '#ef4444',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(248, 113, 113, 0.2)' : '#fee2e2',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Paper>
                    );
                  })}
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2, borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : undefined }} />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>{t('hotelSettings.amenities')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder={t('hotelSettings.amenityPlaceholder')}
                    value={newAmenity}
                    onChange={(e) => setNewAmenity(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddAmenity()}
                    sx={textFieldSx}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddAmenity}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 3,
                      bgcolor: isDark ? '#1e3b8a' : '#3b82f6',
                      boxShadow: isDark ? '0 4px 12px rgba(30, 59, 138, 0.4)' : '0 4px 12px rgba(59, 130, 246, 0.3)',
                      '&:hover': {
                        bgcolor: isDark ? '#1e40af' : '#2563eb',
                        boxShadow: isDark ? '0 6px 16px rgba(30, 59, 138, 0.5)' : '0 6px 16px rgba(59, 130, 246, 0.4)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {t('hotelSettings.add')}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {hotelData.amenities?.map((amenity, index) => (
                    <Chip
                      key={index}
                      label={amenity}
                      onDelete={() => handleRemoveAmenity(index)}
                      sx={{
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                        color: isDark ? theme.palette.text.secondary : '#475569',
                        fontWeight: 500,
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                        '& .MuiChip-deleteIcon': {
                          color: isDark ? theme.palette.text.secondary : '#64748b',
                          '&:hover': {
                            color: isDark ? theme.palette.text.primary : '#475569',
                          },
                        },
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 2: Rules & Policies */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>{t('hotelSettings.hotelRules')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder={t('hotelSettings.rulePlaceholder')}
                    value={newRule}
                    onChange={(e) => setNewRule(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddRule()}
                    sx={textFieldSx}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddRule}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 3,
                      bgcolor: isDark ? '#1e3b8a' : '#3b82f6',
                      boxShadow: isDark ? '0 4px 12px rgba(30, 59, 138, 0.4)' : '0 4px 12px rgba(59, 130, 246, 0.3)',
                      '&:hover': {
                        bgcolor: isDark ? '#1e40af' : '#2563eb',
                        boxShadow: isDark ? '0 6px 16px rgba(30, 59, 138, 0.5)' : '0 6px 16px rgba(59, 130, 246, 0.4)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {t('hotelSettings.add')}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {hotelData.rules?.map((rule, index) => (
                    <Chip
                      key={index}
                      label={rule}
                      onDelete={() => handleRemoveRule(index)}
                      sx={{
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                        color: isDark ? theme.palette.text.secondary : '#475569',
                        fontWeight: 500,
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                        '& .MuiChip-deleteIcon': {
                          color: isDark ? theme.palette.text.secondary : '#64748b',
                          '&:hover': {
                            color: isDark ? theme.palette.text.primary : '#475569',
                          },
                        },
                      }}
                    />
                  ))}
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2, borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : undefined }} />
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>{t('hotelSettings.policies')}</Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.cancellationPolicy')}
                  multiline
                  rows={3}
                  value={hotelData.policies?.cancellation || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      policies: { ...hotelData.policies, cancellation: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.checkInPolicy')}
                  multiline
                  rows={3}
                  value={hotelData.policies?.checkIn || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      policies: { ...hotelData.policies, checkIn: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.checkOutPolicy')}
                  multiline
                  rows={3}
                  value={hotelData.policies?.checkOut || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      policies: { ...hotelData.policies, checkOut: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.paymentPolicy')}
                  multiline
                  rows={3}
                  value={hotelData.policies?.payment || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      policies: { ...hotelData.policies, payment: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.childrenPolicy')}
                  multiline
                  rows={3}
                  value={hotelData.policies?.children || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      policies: { ...hotelData.policies, children: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.petsPolicy')}
                  multiline
                  rows={3}
                  value={hotelData.policies?.pets || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      policies: { ...hotelData.policies, pets: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.smokingPolicy')}
                  multiline
                  rows={3}
                  value={hotelData.policies?.smoking || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      policies: { ...hotelData.policies, smoking: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, color: theme.palette.text.primary }}>{t('hotelSettings.otherPolicies')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder={t('hotelSettings.addOtherPolicy')}
                    value={newPolicyOther}
                    onChange={(e) => setNewPolicyOther(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddPolicyOther()}
                    sx={textFieldSx}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddPolicyOther}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 3,
                      bgcolor: isDark ? '#1e3b8a' : '#3b82f6',
                      boxShadow: isDark ? '0 4px 12px rgba(30, 59, 138, 0.4)' : '0 4px 12px rgba(59, 130, 246, 0.3)',
                      '&:hover': {
                        bgcolor: isDark ? '#1e40af' : '#2563eb',
                        boxShadow: isDark ? '0 6px 16px rgba(30, 59, 138, 0.5)' : '0 6px 16px rgba(59, 130, 246, 0.4)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {t('hotelSettings.add')}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {hotelData.policies?.other?.map((policy: string, index: number) => (
                    <Chip
                      key={index}
                      label={policy}
                      onDelete={() => handleRemovePolicyOther(index)}
                      sx={{
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                        color: isDark ? theme.palette.text.secondary : '#475569',
                        fontWeight: 500,
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                        '& .MuiChip-deleteIcon': {
                          color: isDark ? theme.palette.text.secondary : '#64748b',
                          '&:hover': {
                            color: isDark ? theme.palette.text.primary : '#475569',
                          },
                        },
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 3: Settings */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.timezone')}
                  placeholder="e.g., America/New_York"
                  value={hotelData.settings?.timezone || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      settings: { ...hotelData.settings, timezone: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.currency')}
                  placeholder="e.g., USD, EUR"
                  value={hotelData.settings?.currency || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      settings: { ...hotelData.settings, currency: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.language')}
                  placeholder="e.g., en, tr"
                  value={hotelData.settings?.language || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      settings: { ...hotelData.settings, language: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.checkInTime')}
                  placeholder="e.g., 14:00"
                  value={hotelData.settings?.checkInTime || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      settings: { ...hotelData.settings, checkInTime: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.checkOutTime')}
                  placeholder="e.g., 11:00"
                  value={hotelData.settings?.checkOutTime || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      settings: { ...hotelData.settings, checkOutTime: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.maxGuestsPerRoom')}
                  type="number"
                  value={hotelData.settings?.maxGuests || 0}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      settings: { ...hotelData.settings, maxGuests: parseInt(e.target.value) || 0 },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.starRating')}
                  type="number"
                  inputProps={{ min: 0, max: 5 }}
                  value={hotelData.settings?.starRating || 0}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      settings: { ...hotelData.settings, starRating: parseInt(e.target.value) || 0 },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>

              {/* Guest policies */}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
                  {t('hotelSettings.guestPoliciesTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('hotelSettings.guestPoliciesDescription')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hotelData.settings?.allowEditGuestNameFromPms || false}
                      onChange={(e) =>
                        setHotelData({
                          ...hotelData,
                          settings: {
                            ...hotelData.settings,
                            allowEditGuestNameFromPms: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {t('hotelSettings.allowEditGuestNameFromPms')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('hotelSettings.allowEditGuestNameFromPmsDesc')}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hotelData.settings?.guestMustInitiateContact || false}
                      onChange={(e) =>
                        setHotelData({
                          ...hotelData,
                          settings: {
                            ...hotelData.settings,
                            guestMustInitiateContact: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {t('hotelSettings.guestMustInitiateContact')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('hotelSettings.guestMustInitiateContactDesc')}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {/* GDPR / KVKK */}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
                  {t('hotelSettings.gdprTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('hotelSettings.gdprDescription')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.gdprTextVersion')}
                  value={hotelData.settings?.gdprTextVersion || '1'}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      settings: { ...hotelData.settings, gdprTextVersion: e.target.value },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.gdprTextTr')}
                  multiline
                  minRows={6}
                  value={hotelData.settings?.gdprTexts?.tr || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      settings: {
                        ...hotelData.settings,
                        gdprTexts: {
                          ...(hotelData.settings?.gdprTexts || {}),
                          tr: e.target.value,
                        },
                      },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('hotelSettings.gdprTextEn')}
                  multiline
                  minRows={6}
                  value={hotelData.settings?.gdprTexts?.en || ''}
                  onChange={(e) =>
                    setHotelData({
                      ...hotelData,
                      settings: {
                        ...hotelData.settings,
                        gdprTexts: {
                          ...(hotelData.settings?.gdprTexts || {}),
                          en: e.target.value,
                        },
                      },
                    })
                  }
                  sx={textFieldSx}
                />
              </Grid>

              {/* Night Mode Section */}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.text.primary }}>
                    {t('hotelSettings.nightModeTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('hotelSettings.nightModeDescription')}
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hotelData.settings?.nightModeEnabled || false}
                      onChange={(e) =>
                        setHotelData({
                          ...hotelData,
                          settings: { ...hotelData.settings, nightModeEnabled: e.target.checked },
                        })
                      }
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: isDark ? '#3b82f6' : '#667eea',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: isDark ? '#3b82f6' : '#667eea',
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {t('hotelSettings.enableNightMode')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('hotelSettings.enableNightModeDesc')}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {hotelData.settings?.nightModeEnabled && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={t('hotelSettings.nightModeStartTime')}
                      type="time"
                      placeholder="18:00"
                      value={hotelData.settings?.nightModeStartTime || '18:00'}
                      onChange={(e) =>
                        setHotelData({
                          ...hotelData,
                          settings: { ...hotelData.settings, nightModeStartTime: e.target.value },
                        })
                      }
                      helperText={t('hotelSettings.nightModeStartTimeHelper')}
                      InputLabelProps={{ shrink: true }}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={t('hotelSettings.nightModeEndTime')}
                      type="time"
                      placeholder="08:00"
                      value={hotelData.settings?.nightModeEndTime || '08:00'}
                      onChange={(e) =>
                        setHotelData({
                          ...hotelData,
                          settings: { ...hotelData.settings, nightModeEndTime: e.target.value },
                        })
                      }
                      helperText={t('hotelSettings.nightModeEndTimeHelper')}
                      InputLabelProps={{ shrink: true }}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>{t('hotelSettings.currentConfiguration')}:</strong> {t('hotelSettings.nightModeActiveFrom')}{' '}
                        <strong>{hotelData.settings?.nightModeStartTime || '18:00'}</strong> {t('hotelSettings.to')}{' '}
                        <strong>{hotelData.settings?.nightModeEndTime || '08:00'}</strong> {t('hotelSettings.daily')}.
                        {t('hotelSettings.nightModeActiveDescription')}
                      </Typography>
                    </Alert>
                  </Grid>
                </>
              )}
            </Grid>
          </TabPanel>
        </Paper>
      </Box>
    </Box>
  );
};

export default HotelSettingsPage;


